import React, { Component } from "react";
import VirtualList from "react-tiny-virtual-list";
import classNames from "classnames";
import { animate, emptyFn, getMonth, getWeek, getWeeksInMonth } from "../utils";
import parse from "date-fns/parse";
import startOfMonth from "date-fns/start_of_month";
import Month from "../Month";
import styles from "./MonthList.scss";
import { shape, string, number, bool, arrayOf, oneOf, instanceOf, func, oneOfType, object } from "prop-types";

const AVERAGE_ROWS_PER_MONTH = 5;

export default class MonthList extends Component {
    static propTypes = {
        disabledDates: arrayOf(string),
        disabledDays: arrayOf(number),
        height: number,
        isScrolling: bool,
        locale: shape({
            blank: string,
            headerFormat: string,
            todayLabel: shape({
                long: string,
                short: string
            }),
            weekdays: arrayOf(string),
            weekStartsOn: oneOf([0, 1, 2, 3, 4, 5, 6])
        }),
        maxDate: instanceOf(Date),
        min: instanceOf(Date),
        minDate: instanceOf(Date),
        months: arrayOf(object),
        onDaySelect: func,
        onScroll: func,
        overscanMonthCount: number,
        rowHeight: number,
        selectedDate: instanceOf(Date),
        showOverlay: bool,
        theme: shape({
            floatingNav: shape({
                background: string,
                chevron: string,
                color: string
            }),
            headerColor: string,
            selectionColor: oneOfType([string, func]),
            textColor: shape({
                active: string,
                default: string
            }),
            todayColor: string,
            weekdayColor: string
        }),
        today: instanceOf(Date),
        width: oneOfType([number, string])
    };
    state = {
        scrollTop: this.getDateOffset(this.props.scrollDate),
        scrollDate: this.props.scrollDate
    };


    static getDerivedStateFromProps(nextProps, prevState) {
        const { scrollDate } = nextProps;
        if (scrollDate !== prevState.scrollDate) {
            const { min, rowHeight, locale: { weekStartsOn }, height } = nextProps;
            const weeks = getWeek(startOfMonth(min), parse(scrollDate), weekStartsOn);
            return {
                scrollTop: (weeks * rowHeight - (height - rowHeight / 2) / 2),
                scrollDate: scrollDate
            };
        }
        return null;
    }

    componentDidMount() {
        this.scrollEl = this.VirtualList.rootNode;

    }
    cache = {};
    monthHeights = [];
    memoize = function(param) {
        if (!this.cache[param]) {
            const { locale: { weekStartsOn } } = this.props;
            const [year, month] = param.split(":");
            this.cache[param] = getMonth(year, month, weekStartsOn);
        }
        return this.cache[param];
    };

    _getRef = (instance) => {
        this.VirtualList = instance;
    };

    getMonthHeight = (index) => {
        if (!this.monthHeights[index]) {
            let { locale: { weekStartsOn }, months, rowHeight } = this.props;
            let { month, year } = months[index];
            let weeks = getWeeksInMonth(month, year, weekStartsOn, index === months.length - 1);
            this.monthHeights[index] = weeks * rowHeight;
        }

        return this.monthHeights[index];
    };

    getDateOffset(date) {
        const { min, rowHeight, locale: { weekStartsOn }, height } = this.props;
        const weeks = getWeek(startOfMonth(min), parse(date), weekStartsOn);

        return weeks * rowHeight - (height - rowHeight / 2) / 2;
    }

    scrollToDate = (date, offset = 0, ...rest) => {
        let offsetTop = this.getDateOffset(date);
        this.scrollTo(offsetTop + offset, ...rest);
    };

    scrollTo = (scrollTop = 0, shouldAnimate = false, onScrollEnd = emptyFn) => {
        const onComplete = () => setTimeout(() => {
            this.scrollEl.style.overflowY = "auto";
            onScrollEnd();
        });

        // Interrupt iOS Momentum scroll
        this.scrollEl.style.overflowY = "hidden";

        if (shouldAnimate) {
            /* eslint-disable sort-keys */
            animate({
                fromValue: this.scrollEl.scrollTop,
                toValue: scrollTop,
                onUpdate: (scrollTop, callback) =>
                    this.setState({ scrollTop }, callback),
                onComplete
            });
        } else {
            window.requestAnimationFrame(() => {
                this.scrollEl.scrollTop = scrollTop;
                onComplete();
            });
        }
    };

    renderMonth = ({ index, style }) => {
        let {
            DayComponent,
            disabledDates,
            disabledDays,
            locale,
            maxDate,
            minDate,
            months,
            passThrough,
            rowHeight,
            selected,
            showOverlay,
            theme,
            today,
            dataCollected,
            showTooltip,
            isShowDataCollected,
            adsTypeDate
        } = this.props;

        let { month, year } = months[index];
        let key = `${year}:${month}`;
        let { date, rows } = this.memoize(key);

        return (
            <Month
                showTooltip={showTooltip}
                key={key}
                selected={selected}
                DayComponent={DayComponent}
                monthDate={date}
                disabledDates={disabledDates}
                disabledDays={disabledDays}
                maxDate={maxDate}
                minDate={minDate}
                rows={rows}
                rowHeight={rowHeight}
                isScrolling={false}
                showOverlay={showOverlay}
                today={today}
                theme={theme}
                style={style}
                locale={locale}
                passThrough={passThrough}
                dataCollected={dataCollected}
                isShowDataCollected={isShowDataCollected}
                adsTypeDate={adsTypeDate}
                {...passThrough.Month}
            />
        );
    };

    render() {
        let {
            height,
            isScrolling,
            onScroll,
            overscanMonthCount,
            months,
            rowHeight,
            width
        } = this.props;
        const { scrollTop } = this.state;


        return (
            <VirtualList
                ref={this._getRef}
                width={width}
                height={height}
                itemCount={months.length}
                itemSize={this.getMonthHeight}
                estimatedItemSize={rowHeight * AVERAGE_ROWS_PER_MONTH}
                renderItem={this.renderMonth}
                onScroll={onScroll}
                scrollOffset={scrollTop}
                className={classNames(styles.root, { [styles.scrolling]: isScrolling })}
                style={{ lineHeight: `${rowHeight}px` }}
                overscanCount={overscanMonthCount}
            />
        );
    }
}
